import { useEffect, useRef } from 'react';
import { trackEvent } from '@/utils/gaDataPush';

type MobileNavBehaviorOptions = {
  rootId: string;
  /** Only bind when the drawer is rendered as static HTML; the React tree binds its own handlers. */
  enabled: boolean;
  currencyCode?: string;
  isGlobalEFlag?: boolean;
  isGlobalESite?: boolean;
  /** Mirrors the panel index into React so chrome can react to it (Global-E CTA injection). */
  onPanelChange?: (index: number | null) => void;
};

const OPENED = 'mm-opened';
const SUBOPENED = 'mm-subopened';

function pushNavGaClick(link: HTMLAnchorElement, url: string, label: string) {
  const w = window as any;
  if (typeof w.homePageDataPush === 'function') {
    w.homePageDataPush('top_navigation_click', url, label, 'top nav');
  }
  // Match utils/gaDataPush.handleGaClick: data-event* attrs still drive GA Universal.
  trackEvent({ currentTarget: link } as Parameters<typeof trackEvent>[0]);
}

/**
 * Reimplements `MobileNavigation`'s panel switching and GA on the static island. The category
 * list and submenu panels are plain HTML, so the `currentIndex` state that used to toggle
 * `mm-opened` / `mm-subopened` is applied directly to the DOM and delegated from the root.
 */
export default function useMobileNavBehavior({
  rootId,
  enabled,
  currencyCode,
  isGlobalEFlag,
  isGlobalESite,
  onPanelChange,
}: MobileNavBehaviorOptions) {
  const onPanelChangeRef = useRef(onPanelChange);
  onPanelChangeRef.current = onPanelChange;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const root = document.getElementById(rootId);
    const rootPanel = root?.querySelector<HTMLElement>('[data-be-nav-root-panel]');
    if (!root || !rootPanel) {
      return undefined;
    }

    const panels = () => Array.from(root.querySelectorAll<HTMLElement>('[data-be-nav-panel]'));

    const openPanel = (index: number | null) => {
      panels().forEach(panel => {
        const panelIndex = Number(panel.getAttribute('data-be-nav-panel'));
        panel.classList.toggle(OPENED, panelIndex === index);
      });
      rootPanel.classList.toggle(SUBOPENED, index !== null);
      onPanelChangeRef.current?.(index);
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Back button inside a submenu panel.
      if (target?.closest('[data-be-nav-prev]')) {
        event.preventDefault();
        openPanel(null);
        return;
      }

      const link = target?.closest<HTMLAnchorElement>('a');
      if (!link || !root.contains(link)) {
        return;
      }

      const item = link.closest<HTMLElement>('[data-be-nav-item]');

      // Top-level category: open its panel unless Builder gave the item a real destination.
      if (item) {
        const index = Number(item.getAttribute('data-be-nav-item'));
        const href = link.getAttribute('href') || '';
        const label = (link.getAttribute('data-top-label') || link.textContent || '').trim();

        if (!link.classList.contains('mm-next')) {
          pushNavGaClick(link, '', label);
        }

        if (!href || href === '#') {
          event.preventDefault();
          openPanel(index);
        }
        return;
      }

      // Submenu category links only — skip portaled MainMenu chrome (Sign In / Bag / etc).
      if (link.closest('[data-be-nav-chrome-slot]')) {
        return;
      }
      if (link.closest('[data-be-nav-panel]')) {
        pushNavGaClick(link, link.getAttribute('href') || '', (link.textContent || '').trim());
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return;
      }
      const target = event.target as HTMLElement;
      if (target?.closest('[data-be-nav-prev]')) {
        event.preventDefault();
        openPanel(null);
        return;
      }
      const item = target?.closest<HTMLElement>('[data-be-nav-item]');
      if (item) {
        const href = (target.closest('a')?.getAttribute('href') || '').trim();
        if (!href || href === '#') {
          event.preventDefault();
          openPanel(Number(item.getAttribute('data-be-nav-item')));
        }
      }
    };

    root.addEventListener('click', handleClick);
    root.addEventListener('keydown', handleKeyDown);

    return () => {
      root.removeEventListener('click', handleClick);
      root.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, rootId]);

  // The island is rendered with '$' because the server cannot know the GlobalE selection.
  useEffect(() => {
    if (!enabled || !currencyCode || currencyCode === '$') {
      return;
    }

    const shouldReplace = (isGlobalEFlag && !isGlobalESite) || !isGlobalEFlag;
    if (!shouldReplace) {
      return;
    }

    const root = document.getElementById(rootId);
    root?.querySelectorAll('[data-be-nav-panel] dd a').forEach(link => {
      link.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.includes('$')) {
          node.nodeValue = node.nodeValue.replace(/\$/g, currencyCode);
        }
      });
    });
  }, [enabled, rootId, currencyCode, isGlobalEFlag, isGlobalESite]);
}
